import { GenericAbortSignal } from 'axios';
import {
  ApiResponse,
  SummaryResponseData,
  ProhibitedItemsResponseData,
  DelivertyOptionsResponse,
  StoreDeliveryOptionsResponseData,
  SenderPudoResponseData,
  SenderCourierResponseData,
  ReceiverPudoResponseData,
  ReceiverCourierResponseData,
  ParcelItemResponseData,
  UpdateParcelInsuranceResponseData,
  InsuranceLimitsResponseData,
  SubmitSignatureResponseData,
  SelectedDeliveryOptionsResponseData,
  ListDirectionsResponse,
  CheckDirectionListVersionResponse,
} from '../models/apiResponse';
import { GaAnalyticsIds } from '../models/ga-analytics-ids';
import { IGenericInfoBlock } from '../models/generic-info-block/generic-info-block';
import { HsCodeRequest } from '../models/hsCode';
import { ManualResponse, transformManualResponse } from '../models/manual';
import { StepName } from '../models/parcelCreationFlowNavigation';
import {
  InsuranceCoverage,
  ParcelListShipment,
  Shipment as IShipment,
  CourierPickupSlot,
} from '../models/parcelDeliveryMiles';
import {
  IDeliveryLimitationsRequest,
  IDeliveryLimitationsResponse,
  transformDeliveryLimitationsResponse,
} from '../models/parcelDimensions';
import { PickupWindowsResponseData } from '../models/pickupWindowsModels';
import { StoreCourierRecipientRequest } from '../models/recipient-details/store-courier-recipient-request';
import { StorePudoRecipientRequest } from '../models/recipient-details/store-pudo-recipient-request';
import {
  DeliveryOptionsRequest,
  DeliveryRoute,
} from '../models/shipmentCountryInfo';
import { StoreDeliveryOptionsRequest } from '../models/storeDeliveryOptions';
import { SubmitParcelRequest } from '../models/submitParcelRequest';
import { Phone } from '../models/user';
import { apiRequests as requests } from './http-client';
import {
  FIVE_MINUTE_GET_CACHE_CONFIG,
  FIVE_MINUTE_POST_CACHE_CONFIG,
  FORTY_FIVE_SECOND_POST_CACHE_CONFIG,
} from './cache-config';

interface UpdateData {
  field: keyof IShipment;
  value: unknown;
}

const Shipment = {
  summary: (id: number) =>
    requests
      .get<ApiResponse<SummaryResponseData>>(`/v1/shipment/${id}`)
      .then((response) => ({
        shipment: response.data?.shipment,
        messageBus: response.message_bus,
      })),

  prohibitedItems: (id: number) =>
    requests
      .get<
        ApiResponse<ProhibitedItemsResponseData>
      >(`/v1/shipment/${id}/prohibited-items`)
      .then((response) => response.data?.prohibited_items),

  nonCompensatedItems: (id: number) =>
    requests
      .get<
        ApiResponse<IGenericInfoBlock>
      >(`/v1/shipment/${id}/non-compensated-items`)
      .then((response) => response.data),

  customsAndDuty: (id: number) =>
    requests.get<ApiResponse>(`shipment/${id}/customs-and-duty`),

  deliveryLimitations: (body: IDeliveryLimitationsRequest) =>
    requests
      .post<
        ApiResponse<IDeliveryLimitationsResponse>
      >(`/v1/shipment/delivery-limitations`, body, FIVE_MINUTE_POST_CACHE_CONFIG)
      .then((response) => transformDeliveryLimitationsResponse(response.data)),

  taxes: (shipmentId: number) =>
    requests
      .get<ApiResponse<IGenericInfoBlock>>(`/v1/shipment/${shipmentId}/taxes`)
      .then((response) => response?.data),

  // step 2 preparation
  deliveryOptionsV2: (request: DeliveryOptionsRequest) =>
    requests
      .post<
        ApiResponse<DelivertyOptionsResponse>
      >('/v1/shipment/delivery-options-v3', request, FORTY_FIVE_SECOND_POST_CACHE_CONFIG)
      .then((response) => ({
        data: response.data,
        messageBus: response.message_bus ?? [],
      })),

  deliveryHints: (
    departureCountryCode: string,
    destinationCountryCode: string
  ) =>
    requests
      .post<ApiResponse<IGenericInfoBlock>>(
        '/v1/shipment/delivery-hints',
        {
          departure: departureCountryCode,
          destination: destinationCountryCode,
        },
        FIVE_MINUTE_POST_CACHE_CONFIG
      )
      .then((response) => response.data),

  // step 3 preparation
  storeDeliveryOptions: (deliveryOptions: StoreDeliveryOptionsRequest) =>
    requests
      .post<
        ApiResponse<StoreDeliveryOptionsResponseData>
      >('/v1/shipment/store-delivery-options', deliveryOptions)
      .then((response) => response.data?.shipment),

  // step 4 preparation (pudo)
  senderPudo: (
    storePudoRequest: StorePudoRecipientRequest,
    shipmentId: number,
    stepName: StepName
  ) =>
    requests
      .post<
        ApiResponse<SenderPudoResponseData>
      >(`/v1/shipment-steps/${shipmentId}/${stepName}`, storePudoRequest)
      .then((response) => response),
  // step 4 preaparation (courier or concierge)
  senderCourier: (
    storeCourierRequest: StoreCourierRecipientRequest,
    shipmentId: number,
    stepName: StepName
  ) =>
    requests
      .post<
        ApiResponse<SenderCourierResponseData>
      >(`/v1/shipment-steps/${shipmentId}/${stepName}`, storeCourierRequest)
      .then((response) => response),

  // step 5 preparation (pudo)
  receiverPudo: (
    storePudoRequest: StorePudoRecipientRequest,
    shipmentId: number
  ) =>
    requests
      .post<
        ApiResponse<ReceiverPudoResponseData>
      >(`/v1/shipment-steps/${shipmentId}/receiver-details-pudo`, storePudoRequest)
      .then((response) => response),
  // step 5 preparation (courier)
  receiverCourier: (
    storeCourierRequest: StoreCourierRecipientRequest,
    shipmentId: number
  ) =>
    requests
      .post<
        ApiResponse<ReceiverCourierResponseData>
      >(`/v1/shipment-steps/${shipmentId}/receiver-details-courier`, storeCourierRequest)
      .then((response) => response),

  // step 5 parcel content management
  addParcelItem: (item: HsCodeRequest, shipmentId: number) =>
    requests
      .put<
        ApiResponse<ParcelItemResponseData>
      >(`/v1/shipment/${shipmentId}/parcel-content`, item)
      .then((response) => response.data?.shipment),

  parcelContent: (shipmentId: number) =>
    requests
      .post<
        ApiResponse<ParcelItemResponseData>
      >(`/v1/shipment-steps/${shipmentId}/parcel-content`)
      .then((response) => ({
        messageBus: response.message_bus ?? [],
      })),

  updateParcelItem: (item: HsCodeRequest, shipmentId: number) =>
    requests
      .post<
        ApiResponse<ParcelItemResponseData>
      >(`/v1/shipment/${shipmentId}/parcel-content/${item.id}`, item)
      .then((response) => response.data?.shipment),

  deleteParcelItem: (itemId: number, shipmentId: number) =>
    requests
      .del<
        ApiResponse<ParcelItemResponseData>
      >(`/v1/shipment/${shipmentId}/parcel-content/${itemId}`)
      .then((response) => response.data?.shipment),

  updateParcelInsurance: (
    shipmentId: number,
    insurance: { insurance: number }
  ) =>
    requests
      .post<
        ApiResponse<UpdateParcelInsuranceResponseData>
      >(`/v1/shipment/${shipmentId}/insurance`, insurance)
      .then((response) => response.data?.shipment),

  getParcelInsuranceLimits: (shipmentId: number) =>
    requests
      .get<
        ApiResponse<InsuranceLimitsResponseData>
      >(`/v1/shipment/${shipmentId}/insurance-limits`)
      .then((response) => ({
        min_insurance: response.data!.min_insurance,
        max_insurance: response.data!.max_insurance,
        currency: response.data!.currency,
      })),

  getDomesticInsuranceLimits: (
    senderCountryCode: string,
    receiverCountryCode: string,
    currency?: string
  ) =>
    requests
      .get<
        ApiResponse<InsuranceLimitsResponseData>
      >(`/v1/shipment/insurance-limits/?sender_country=${senderCountryCode}&receiver_country=${receiverCountryCode}${currency ? `&currency=${currency}` : ''}`, FIVE_MINUTE_GET_CACHE_CONFIG)
      .then((response) => response.data),

  updateInsuranceCoverage: (shipmentId: number, coverage: InsuranceCoverage) =>
    requests
      .post<
        ApiResponse<UpdateParcelInsuranceResponseData>
      >(`/v1/shipment/${shipmentId}/insurance-mode`, { mode: coverage })
      .then((response) => response.data?.shipment),

  submitParcel: (shipmentId: number, request: SubmitParcelRequest) =>
    requests
      .post<ApiResponse>(`/v1/shipment/${shipmentId}/submit`, request)
      .then((response) => response.data),

  submitSignature: (signature: FormData, shipmentId: number) =>
    requests
      .post<
        ApiResponse<SubmitSignatureResponseData>
      >(`/v1/shipment/${shipmentId}/signature`, signature)
      .then((response) => response.data?.shipment),

  fetchSignature: (shipmentId: number) =>
    requests
      .get<Blob>(`/v1/shipment/${shipmentId}/signature`, {
        responseType: 'blob',
      })
      .then((response) => response),

  shipment: (query: string, signal?: GenericAbortSignal) =>
    requests
      .get<
        ApiResponse<ParcelListShipment[]>
      >(`/v1/shipment?${query}`, { signal })
      .then((response) => response),

  manual: (shipmentId: number, signal?: GenericAbortSignal) =>
    requests
      .get<
        ApiResponse<ManualResponse>
      >(`/v1/manual/shipment/${shipmentId}`, { signal })
      .then((response) => transformManualResponse(response)),

  confirmManual: (shipmentId: number) =>
    requests.post(`/v1/manual/shipment/${shipmentId}/confirm-manual`),

  onboarding: (signal?: GenericAbortSignal) =>
    requests
      .get<ApiResponse<ManualResponse>>(`/v1/manual/onboarding`, {
        ...FIVE_MINUTE_GET_CACHE_CONFIG,
        signal,
      })
      .then((response) => transformManualResponse(response)),

  update: (shipmentId: number, data: UpdateData) =>
    requests
      .post<
        ApiResponse<ParcelListShipment>
      >(`/v1/shipment/${shipmentId}/update`, data)
      .then((response) => response.data),

  payment: (shipmentId: number, request: GaAnalyticsIds) =>
    requests
      .post<
        ApiResponse<{ link: string }>
      >(`/v1/shipment/${shipmentId}/payment`, request)
      .then((response) => response.data?.link),

  label: (shipmentId: number) =>
    requests
      .get<Blob>(`/v1/shipment/${shipmentId}/label`, {
        responseType: 'blob',
      })
      .then((response) => response),

  getBarcodeFile: (shipmentId: number | string) =>
    requests
      .get<Blob>(`/v1/shipment/${shipmentId}/barcode/file`, {
        responseType: 'blob',
        headers: {
          Accept: 'image/png',
        },
      })
      .then((response) => response),

  sendVerificationMessage: (phone: Phone | null) =>
    requests.post<ApiResponse>('/v1/shipment/send-verification-message', {
      phone_country_code: phone?.country_code,
      phone: phone?.phone_number,
    }),

  verifyPhone: (phoneCountryCode: string, phoneNumber: string, code: string) =>
    requests
      .post<ApiResponse>('/v1/shipment/verify-phone', {
        phone_country_code: phoneCountryCode,
        phone: phoneNumber,
        code,
      })
      .then((authResponse) => authResponse.data),

  invoice: (shipmentId: number) =>
    requests
      .get<Blob>(`/v1/shipment/${shipmentId}/invoice`, {
        responseType: 'blob',
      })
      .then((response) => response),

  pickupWindows: (shipmentId: number, timezone: string) =>
    requests
      .post<
        ApiResponse<PickupWindowsResponseData>
      >(`/v1/shipment/${shipmentId}/pickup-windows`, { timezone })
      .then((response) => response.data),

  storePickupWindows: (shipmentId: number, pickupSlot: CourierPickupSlot) =>
    requests
      .post<ApiResponse<{ link: string }>>(
        `/v1/shipment/${shipmentId}/store-pickup-windows`,
        {
          courier_pickup: pickupSlot,
        }
      )
      .then((response) => response.data),

  getSelectedDeliveryOptions: (shipmentId: number) =>
    requests
      .get<
        ApiResponse<SelectedDeliveryOptionsResponseData>
      >(`/v1/shipment/${shipmentId}/selected-delivery-options-v3`)
      .then((response) => response.data),

  shipmentSteps: (shipmentId: number) =>
    requests
      .get<ApiResponse>(`/v1/shipment-steps/${shipmentId}`)
      .then((response) => ({
        navigation: response.data?.navigation,
        shipment: response.data?.shipment,
        messageBus: response.message_bus,
      })),

  listDirections: () =>
    requests
      .get<ApiResponse<ListDirectionsResponse>>('/v1/shipment/directions/list')
      .then((response) => response.data),

  checkDirectionsListVersion: (version: string) =>
    requests
      .post<
        ApiResponse<CheckDirectionListVersionResponse>
      >(`/v1/shipment/directions/check`, { version })
      .then((response) => response.data),

  registerRouteExpectation: (request: DeliveryRoute) =>
    requests
      .post<ApiResponse>(`/v1/shipment/expect-route`, request)
      .then((response) => response.data),
};

export default Shipment;
